import { Button, Stack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import usePagination from './usePagination';
import { Icons } from '../../Icons';

interface PaginationProps {
  totalItems: number;
  pageSize: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  variant?: string;
}

export function Pagination({
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
  variant = 'pagination',
}: PaginationProps) {
  const { items, hasPrev, hasNext } = usePagination({
    page: currentPage,
    count: Math.ceil(totalItems / pageSize),
    siblingCount: 1,
    boundaryCount: 1,
  });

  if (!totalItems) {
    return null;
  }

  if (totalItems <= pageSize) {
    return null;
  }

  return (
    <Stack direction="row" spacing={1}>
      <PageButton
        disabled={!hasPrev}
        variant={variant}
        onClick={() => onPageChange(currentPage - 1)}
      >
        <Icons.ArrowLeftThick />
      </PageButton>
      {items.map((item) =>
        item === 'start-ellipsis' || item === 'end-ellipsis' ? (
          <Ellipsis key={item} />
        ) : (
          <PageButton
            active={item === currentPage}
            key={item}
            variant={variant}
            onClick={() => onPageChange(item)}
          >
            {item}
          </PageButton>
        ),
      )}
      <PageButton
        disabled={!hasNext}
        variant={variant}
        onClick={() => onPageChange(currentPage + 1)}
      >
        <Icons.ArrowRightThick />
      </PageButton>
    </Stack>
  );
}

const PageButton = ({
  active,
  disabled,
  children,
  onClick,
  variant,
}: {
  active?: boolean;
  disabled?: boolean;
  children: ReactNode;
  onClick: () => void;
  variant: string;
}) => {
  return (
    <Button
      size="sm"
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      isActive={active}
    >
      {children}
    </Button>
  );
};

const Ellipsis = () => {
  return (
    <Button
      color="white"
      _light={{ color: 'black' }}
      size="sm"
      aria-hidden="true"
      variant="ghost"
      disabled={true}
      _disabled={{ cursor: 'default' }}
    >
      ...
    </Button>
  );
};
